/**
 * The task page header: the way back to the board, the reference, the
 * title, and the chips — state, type, size, the suggested or approved
 * priority, the merge expectation and the execution state.
 */

import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';

import type { TaskDetail } from '../../api.js';
import { TaskStateChip, useWorkspaceCopy } from '../../components.js';
import { Tag } from '../../ui.js';

export function TaskDetailHeader({ task }: { readonly task: TaskDetail }): ReactNode {
  const copy = useWorkspaceCopy();
  const isBacklog = task.status === 'BACKLOG';

  return (
    <>
      <p style={{ marginBottom: 'var(--space-sm)' }}>
        <Link
          to="/tasks"
          style={{ color: 'var(--text-link)', font: 'var(--type-body-md)' }}
        >
          {copy.task.backToBoard}
        </Link>
      </p>

      <header style={{ marginBottom: 'var(--space-lg)' }}>
        <p
          className="mj-ltr"
          dir="ltr"
          style={{ font: 'var(--type-body-tabular)', color: 'var(--text-muted)' }}
        >
          {task.reference}
        </p>
        <h1
          style={{
            font: 'var(--type-display-md)',
            letterSpacing: 'var(--type-display-md-tracking)',
            color: 'var(--text-primary)',
            margin: '4px 0 10px',
          }}
        >
          {task.title}
        </h1>
        <div
          style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center' }}
        >
          <TaskStateChip status={task.status} />
          <Tag tone="neutral">{copy.types[task.taskType] ?? task.taskType}</Tag>
          <Tag tone="neutral">
            {copy.difficulties[task.difficulty] ?? task.difficulty}
          </Tag>
          {isBacklog && task.suggestedPriority !== null ? (
            <Tag dashed>
              {copy.task.suggested(
                copy.priorities[task.suggestedPriority] ?? task.suggestedPriority,
              )}
              {' — '}
              {copy.task.suggestedNotApproved}
            </Tag>
          ) : null}
          {!isBacklog && task.priority !== null ? (
            <Tag tone="info" title={copy.task.authoritativePriority}>
              {copy.priorities[task.priority] ?? task.priority}
            </Tag>
          ) : null}
          {task.requiresMerge ? (
            <Tag tone="warning">{copy.task.requiresMerge}</Tag>
          ) : null}
          {task.executionState === 'INDEPENDENT' ? (
            <Tag tone="neutral">{copy.execution.independent}</Tag>
          ) : task.executionState === 'DEPENDENCIES_CLEARED' ? (
            <Tag tone="success">{copy.execution.cleared}</Tag>
          ) : (
            <Tag tone="warning">
              {copy.execution.blockedBy}{' '}
              <span className="mj-ltr" dir="ltr">
                {task.blockedBy.map((blocker) => blocker.reference).join(', ')}
              </span>
              {task.unresolvedCount > task.blockedBy.length
                ? ` ${copy.execution.more(task.unresolvedCount - task.blockedBy.length)}`
                : ''}
            </Tag>
          )}
        </div>
        {isBacklog && task.executionState === 'BLOCKED' ? (
          <p
            style={{
              font: 'var(--type-caption)',
              color: 'var(--text-secondary)',
              marginTop: 8,
            }}
          >
            {copy.execution.approvalBlocked}
          </p>
        ) : null}
      </header>
    </>
  );
}
